/**
 * Types for the Authentication tab's identity-provider management.
 *
 * Kept separate from the read-only IdentityProviderContext: that one is consumed
 * by unrelated screens (the create-user provider picker) which must not see
 * management state, and the codebase already carries two overlapping provider
 * shapes without widening the read model further.
 */

/**
 * Wizard-local only. Selects which setup guidance to show; never sent to the API.
 * Google Workspace is absent: its console offers only a one-time metadata file
 * download, not the fetchable URL this wizard requires.
 */
export type IdpGuidanceType = 'ENTRA' | 'OKTA' | 'SHIBBOLETH' | 'OTHER';

/**
 * Title-case suffix per type, building every per-type i18n key from one place
 * (`settings.authentication.wizard.type<Suffix>`, `...guidance<Suffix>`).
 * Shared by the wizard's type picker and the setup guide's own.
 */
export const GUIDED_TYPE_SUFFIX: Record<IdpGuidanceType, string> = {
    ENTRA: 'Entra',
    OKTA: 'Okta',
    SHIBBOLETH: 'Shibboleth',
    OTHER: 'Other',
};

export enum ProvisioningState {
    IDLE = 'IDLE',
    SUBMITTING = 'SUBMITTING',
    /** Submit itself was rejected — e.g. metadata could not be fetched or parsed. */
    SUBMIT_REJECTED = 'SUBMIT_REJECTED',
    SUBMITTED = 'SUBMITTED',
    PROVISIONING = 'PROVISIONING',
    PROVISIONED = 'PROVISIONED',
    FAILED = 'FAILED',
    /**
     * Polling hit its attempt cap. Distinct from FAILED: the backend never said
     * it failed, we stopped waiting — but the user still gets a definite outcome.
     */
    TIMED_OUT = 'TIMED_OUT',
}

/** Terminal states — polling must not continue past any of these. */
export const TERMINAL_PROVISIONING_STATES: ReadonlySet<ProvisioningState> =
    new Set([
        ProvisioningState.PROVISIONED,
        ProvisioningState.FAILED,
        ProvisioningState.TIMED_OUT,
        ProvisioningState.SUBMIT_REJECTED,
    ]);

/** States of an add attempt that has not finished yet. */
export const IN_FLIGHT_PROVISIONING_STATES: ReadonlySet<ProvisioningState> =
    new Set([
        ProvisioningState.SUBMITTING,
        ProvisioningState.SUBMITTED,
        ProvisioningState.PROVISIONING,
    ]);

export interface AddIdentityProviderInput {
    displayName: string;
    metadataUrl: string;
    /** The SAML attribute carrying the user identifier. */
    nameIdentifier: string;
    /** UI guidance selection only — intentionally not part of the request body. */
    idpType?: IdpGuidanceType;
}

/**
 * One add attempt of an identity provider, from submit until it is no longer
 * needed.
 *
 * The list shows a row for it until the provider list includes the new
 * provider. The add wizard shows it too, but never changes it.
 */
export interface AddAttempt {
    /** Created on the client at submit, so it exists before any server id. */
    key: string;
    input: AddIdentityProviderInput;
    state: ProvisioningState;
    /** Set once the server accepts the add attempt. */
    provisioningId?: string;
    /** Set once provisioned, if the server reports it. */
    identityProviderId?: string;
    /** Auth0 connection name, set once provisioned. */
    connectionName?: string;
    /** Why the server refused the submit, if it gave a known reason. */
    rejectedReason?: AddFailureReason;
    /** Why provisioning failed, if the server said. */
    failureKind?: ProvisioningStatusResponse['failureKind'];
}

/** Request body for the add endpoint. Note there is no type field: always SAML. */
export interface AddIdentityProviderRequest {
    displayName: string;
    metadataUrl: string;
    mapping: { user_id: string };
}

export interface ProvisioningStatusResponse {
    status: 'SUBMITTED' | 'PROVISIONING' | 'PROVISIONED' | 'FAILED';
    identityProviderId?: string;
    /**
     * Auth0 connection name, reported once PROVISIONED. The setup guide's
     * service-provider metadata URL is keyed by connection, and for a just-created
     * provider nothing else client-side knows it.
     */
    connectionName?: string;
    /**
     * Why a FAILED provisioning failed. A kind rather than free text, because the
     * auth broker supplies no reason to put in a string.
     *
     * REFUSED   -- rejected; retrying unchanged fails again, so fix something.
     * TIMED_OUT -- the server's bounded wait ran out; the config may be fine.
     *
     * Never present these as one outcome: "fix this" and "try again" send the
     * admin down different paths.
     */
    failureKind?: 'REFUSED' | 'TIMED_OUT';
}

/**
 * The tenant's service-provider values, pasted by the admin into their IdP. Read
 * from the backend, never derived client-side: they follow from the Auth0
 * connection, and a guessed URL fails silently at sign-in, far from its cause.
 */
export interface ServiceProviderDetails {
    /** SAML entity ID — "Identifier" in Entra, "Audience URI" in Okta. */
    identifier: string;
    /** Assertion consumer service URL — "Reply URL" / "Single sign-on URL". */
    replyUrl: string;
}

/**
 * Why a connection cannot be edited. Exactly one is ever returned, and the
 * server decides -- the read-only dialog is presentation, not the control.
 *
 * Declared as a list so the runtime guard and the type come from one place: the
 * server also returns prose for a rejected request, and anything not in this
 * list must fall back to the generic message rather than be shown to an admin.
 */
export const NOT_EDITABLE_REASONS = [
    'CLONED',
    'OWN_CONNECTION',
    'LEGACY_NO_METADATA_URL',
    'CHECK_UNAVAILABLE',
] as const;
export type NotEditableReason = (typeof NOT_EDITABLE_REASONS)[number];

/** Why an edit was refused, as a code mapped to local copy. */
export const UPDATE_FAILURE_REASONS = [
    'METADATA_OR_ATTRIBUTE_INVALID',
    'BROKER_UNAVAILABLE',
] as const;
export type UpdateFailureReason = (typeof UPDATE_FAILURE_REASONS)[number];

/**
 * What the connection endpoints return, and nothing else. The broker's
 * connection body carries more (enabledApplications, signingCert, entityId,
 * checkNameQualifier); restricting it is a security control.
 */
export interface IdpConnection {
    displayName: string;
    /** Absent when the connection was configured with a metadata FILE */
    metadataUrl?: string;
    mapping: { user_id: string };
    /** Display only for admin info */
    certificateExpiresAt?: string;
    editable: boolean;
    notEditableReason?: NotEditableReason;
}

/**
 * What an edit sends.
 */
export interface IdpConnectionUpdate {
    displayName: string;
    metadataUrl: string;
    mapping: { user_id: string };
}

/** Editing is a synchronous PATCH, so no provisioning phase and no polling. */
export enum UpdateState {
    IDLE = 'IDLE',
    SAVING = 'SAVING',
    SAVED = 'SAVED',
    FAILED = 'FAILED',
}

/**
 * Why a delete is refused. Exactly one is ever returned.
 *
 * CHECK_UNAVAILABLE means the guardrails could not be established -- the users
 * service was unreadable, or the read exceeded timeout
 */
export type DeleteBlockedReason =
    | 'HAS_USERS'
    | 'LAST_IDP'
    | 'OWN_IDP'
    | 'CHECK_UNAVAILABLE';

export interface DeleteEligibilityResponse {
    eligible: boolean;
    blockedReason?: DeleteBlockedReason;
    usersAssignedCount?: number;
}

export enum DeleteState {
    IDLE = 'IDLE',
    CHECKING = 'CHECKING',
    /** Eligibility passed — a confirmation may now be offered. */
    CONFIRMABLE = 'CONFIRMABLE',
    /** Eligibility refused. The reason carries the message the admin sees. */
    BLOCKED = 'BLOCKED',
    DELETING = 'DELETING',
    DELETED = 'DELETED',
    /**
     * Refused despite eligibility passing. The server enforces the guardrails
     * independently and returns no detail, so nothing specific can be shown.
     */
    DELETE_REFUSED = 'DELETE_REFUSED',
    ALREADY_DELETED = 'ALREADY_DELETED',
}

export enum MigrationState {
    IDLE = 'IDLE',
    TRIGGERING = 'TRIGGERING',
    /** Either phase is still running. Never present this as complete. */
    IN_PROGRESS = 'IN_PROGRESS',
    SUCCEEDED = 'SUCCEEDED',
    FAILED = 'FAILED',
    TIMED_OUT = 'TIMED_OUT',
}

/**
 * Raw statuses the migration API reports. REGISTERING is a second workflow that
 * runs *after* migration finishes, so only SUCCEEDED means done — mapping
 * anything earlier to "complete" is the production defect this corrects.
 */
export type RawMigrationStatus =
    | 'STARTED'
    | 'MIGRATING'
    | 'REGISTERING'
    | 'SUCCEEDED'
    | 'FAILED';

/**
 * Mirrors AuditLogEntryModel in bbdata-auth-prov. The service is the source of
 * truth for this shape; do not diverge from it here.
 */
export interface AuditLogEntry {
    occurredAt: number;
    actorDisplay: string;
    actionType: string;
    idpDisplayName?: string;
    /** Display-only, open-ended: keys may be added server-side at any time. */
    details: Record<string, string>;
}

export interface AuditLogResponse {
    entries: AuditLogEntry[];
    /** The sort key of the next page, or absent on the last one. */
    nextCursor?: number;
}

export const ADD_FAILURE_REASONS = ['METADATA_URL_UNREADABLE'] as const;
export type AddFailureReason = (typeof ADD_FAILURE_REASONS)[number];
